import { useCallback } from 'react';

import { useToastStore } from '@pkg/components';
import {
    type MutationWishlistsCollectiblesAddArgs,
    type MutationWishlistsCollectionsAddArgs,
    Privacy,
    type WishList,
} from '@pkg/graphql/codegen/graphql.ts';
import { type SupportedChain } from '@pkg/types';

import { useAuthTokens, useWishlistMetadataStore } from '@app/store/zustand';

import { COLLECTION_COLLECTIBLES_CACHE } from '../marketplace/assets/useGetCollectibles.ts';
import { GQL_COLLECTIONS_QUERY_KEY } from '../marketplace/assets/useGetCollections.ts';
import {
    useAddWishlistItemsMutation,
    useFetchWishlists,
    USER_WISHLISTS,
    useRemoveWishlistItemsMutation,
    useWishlistCRUD,
} from '../queryHooks/useWishlists.ts';
import { useAppSelector } from '../redux/useAppSelector.ts';
import { useQueryInvalidation } from '../useQueryInvalidation.ts';

const WISHLIST_QUERY_KEYS = [USER_WISHLISTS, COLLECTION_COLLECTIBLES_CACHE, GQL_COLLECTIONS_QUERY_KEY];

type AddItemParams = {
    slug?: string;
    name?: string;
    preventToast?: boolean;
} & {
    collectiblesIds?: MutationWishlistsCollectiblesAddArgs['collectiblesIds'];
    collectionsIds?: MutationWishlistsCollectionsAddArgs['collectionsIds'];
};
type MoveItemParams = { fromWishlist: WishList; toWishlist: WishList; token?: string; collection?: string };

export function useAddWishlistItems() {
    const invalidateQueries = useQueryInvalidation(WISHLIST_QUERY_KEYS);
    const { addToast } = useToastStore();
    const { addCollectible, addCollection } = useAddWishlistItemsMutation();
    const userName = useAppSelector((state) => state.user.userClass?.user?.user.username);
    const setManageItemData = useWishlistMetadataStore((s) => s.setManageItemData);
    const userId = useAuthTokens.getState().currentUserId;
    const { createWishlist } = useWishlistCRUD();
    const { fetchSingleWishlist } = useFetchWishlists();

    const createNewList = useCallback(async () => {
        const name = userName ? `${userName}'s wishlist` : 'My wishlist';
        const newWishlistRes = await createWishlist.mutateAsync({
            newWishlist: {
                name,
                privacy: Privacy.Public,
            },
        });

        return newWishlistRes?.data?.wishlistsCreate;
    }, [createWishlist, userName]);
    const isLoadingAddItem = addCollectible.isPending || addCollection.isPending;
    const addWishlistItem = useCallback(
        async (args: AddItemParams) => {
            const baseFilters = { owned: { internalId: userId || '', primaryYat: '' } };
            const filters = args.slug?.length ? { slugs: [args.slug] } : baseFilters;
            const currentWishlist = await fetchSingleWishlist({
                variables: {
                    includeContents: true,
                    filter: filters,
                },
            });

            let addParams = args;

            if (currentWishlist) {
                addParams = {
                    ...args,
                    slug: currentWishlist.slug,
                    name: currentWishlist.name,
                };
            }
            let wishlistData = currentWishlist;
            const shouldCreate = Boolean(!args.slug?.length && !currentWishlist?.slug.length);

            if (shouldCreate) {
                const createdWishlistData = await createNewList();
                if (createdWishlistData) {
                    wishlistData = createdWishlistData;
                    addParams = {
                        ...args,
                        slug: createdWishlistData.slug,
                        name: createdWishlistData.name,
                    };
                }
            }

            if (args.collectiblesIds && args?.collectiblesIds?.[0]) {
                return addCollectible
                    .mutateAsync(addParams as MutationWishlistsCollectiblesAddArgs)
                    .then((r) => {
                        invalidateQueries();
                        if (!r?.errors && !args.preventToast && args.collectiblesIds !== undefined) {
                            const [chain, contract, tokenId] = args.collectiblesIds[0].split('.');
                            addToast({
                                emoji: '⭐️',
                                message: `Collectible has been added to ${addParams?.name}`,
                                messageAction: {
                                    onAction: () =>
                                        setManageItemData({
                                            chain,
                                            token: {
                                                contract,
                                                id: tokenId,
                                            },
                                            wishlistData,
                                        }),
                                    copy: 'Manage',
                                },
                            });
                        }
                    })
                    .catch((e) => e);
            }

            if (args.collectionsIds && args.collectionsIds[0]) {
                return addCollection
                    .mutateAsync(addParams as MutationWishlistsCollectionsAddArgs)
                    .then((r) => {
                        if (r.data && !args.preventToast && args.collectionsIds !== undefined) {
                            const [chain, slug] = args.collectionsIds[0].split('.');
                            addToast({
                                emoji: '⭐️',
                                message: `Collection has been added to ${addParams.name}`,
                                messageAction: {
                                    onAction: () =>
                                        setManageItemData({
                                            chain,
                                            slug,
                                            wishlistData,
                                        }),
                                    copy: 'Manage',
                                },
                            });
                        }
                    })
                    .catch((e) => e)
                    .finally(invalidateQueries);
            }
        },
        [
            addCollectible,
            addCollection,
            addToast,
            createNewList,
            fetchSingleWishlist,
            invalidateQueries,
            setManageItemData,
            userId,
        ],
    );

    return { addWishlistItem, isLoadingAddItem };
}

export function useMoveWishlistItems() {
    const invalidateQueries = useQueryInvalidation(WISHLIST_QUERY_KEYS);
    const setManageItemData = useWishlistMetadataStore((s) => s.setManageItemData);
    const { addToast } = useToastStore();
    const { addWishlistItem } = useAddWishlistItems();
    const { removeCollectible, removeCollection } = useRemoveWishlistItemsMutation();
    return useCallback(
        async ({ fromWishlist, toWishlist, token, collection }: MoveItemParams) => {
            let completed = false;

            try {
                if (token) {
                    removeCollectible.mutateAsync({ slug: fromWishlist.slug, collectiblesIds: [token] }).then((r) => {
                        if (!r.errors) {
                            addWishlistItem({
                                collectiblesIds: [token],
                                slug: toWishlist.slug,
                                preventToast: true,
                            });
                        }
                    });
                }

                if (collection) {
                    removeCollection
                        .mutateAsync({ slug: fromWishlist.slug, collectionsIds: [collection] })
                        .then((r) => {
                            if (!r.errors) {
                                addWishlistItem({
                                    collectionsIds: [collection],
                                    slug: toWishlist.slug,
                                    preventToast: true,
                                });
                            }
                            if (r.errors?.[0]?.message) {
                                addToast({ minimal: true, message: r.errors?.[0]?.message });
                            }
                        });
                }
                completed = true;
            } catch (_e) {
                addToast({ minimal: true, message: 'Something went wrong' });
            }

            const [chain, contract, tokenId] = token?.split('.') || [];
            const [_chain, slug] = collection?.split('.') || [];
            const itemData = tokenId
                ? { token: { contract, id: tokenId as string }, chain: chain as SupportedChain }
                : { chain: _chain as SupportedChain, slug: slug as string };

            if (completed) {
                invalidateQueries();
                addToast({
                    emoji: '💥',
                    message: `${token ? 'Collectible' : 'Collection'} has been moved to ${toWishlist.name}`,
                    messageAction: {
                        onAction: () =>
                            setManageItemData({
                                ...itemData,
                                wishlistData: toWishlist,
                            }),
                        copy: 'Manage',
                    },
                });
            }
        },
        [addToast, addWishlistItem, invalidateQueries, removeCollectible, removeCollection, setManageItemData],
    );
}
